import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Building2, Package, Webhook, TrendingUp, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SuperAdminOverview = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadOverview();
    const interval = setInterval(loadOverview, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadOverview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/super-admin/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Load overview error:', error);
      toast.error('Failed to load overview');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <img 
          src="/flotto-logo.png"
          alt="Flotto"
          className="h-16 w-16 object-contain"
        />
        <div>
          <h1 className="text-4xl font-bold">Platform Overview</h1>
          <p className="text-gray-500 mt-1">Flotto SaaS Analytics</p>
        </div>
      </div>

      {/* Tenant Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Tenants</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.tenants?.total || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.tenants?.active || 0} active, {stats?.tenants?.suspended || 0} suspended
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Drivers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.drivers?.total || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.drivers?.active_shifts || 0} active shifts
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Deliveries Today</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.deliveries?.today || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.deliveries?.completed_today || 0} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Webhooks</CardTitle>
            <Webhook className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.webhooks?.pending || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.webhooks?.failed || 0} failed, {stats?.webhooks?.dlq || 0} in DLQ
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Plan Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Plan Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(stats?.tenants?.by_plan || {}).map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant={plan === 'enterprise' ? 'default' : plan === 'pro' ? 'secondary' : 'outline'}>
                    {plan.toUpperCase()}
                  </Badge>
                  <span className="text-sm">{count} tenants</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        plan === 'enterprise' ? 'bg-blue-600' : plan === 'pro' ? 'bg-green-600' : 'bg-gray-400'
                      }`}
                      style={{ width: `${(count / stats?.tenants?.total) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-500">
                    {Math.round((count / stats?.tenants?.total) * 100)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Alerts */}
      {(stats?.webhooks?.failed > 0 || stats?.webhooks?.dlq > 0) && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <AlertCircle className="h-5 w-5" />
              Action Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {stats?.webhooks?.failed > 0 && (
                <li>• {stats.webhooks.failed} webhooks failed - check Webhooks tab</li>
              )}
              {stats?.webhooks?.dlq > 0 && (
                <li>• {stats.webhooks.dlq} webhooks in Dead Letter Queue - manual intervention needed</li>
              )}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SuperAdminOverview;
